import { Resource } from './Resource.js';
import { getDistance } from '../utils/math.js';
import { RESOURCE_CONFIG, ANIMATION_CONFIG } from '../config.js';

export class Nebula extends Resource {
  constructor(x, y) {
    super(x, y, 0); // Nebulas don't have a fixed amount
    
    // Nebula-specific properties
    this.radius = RESOURCE_CONFIG.NEBULA_RADIUS;
    this.gatherers = new Set();
    this.particles = this.createParticles();
    this.cloudPhase = Math.random() * Math.PI * 2;
    
    // Organic tracking
    this.totalOrganics = RESOURCE_CONFIG.NEBULA_TOTAL_ORGANICS;
    this.remainingOrganics = this.totalOrganics;
    this.isDepleted = false;
    this.depletedTime = null;
    this.opacity = 1.0;
  }
  
  createParticles() {
    const particles = [];
    for (let i = 0; i < ANIMATION_CONFIG.NEBULA_PARTICLE_COUNT; i++) {
      particles.push({
        angle: Math.random() * Math.PI * 2,
        distance: Math.random() * this.radius,
        speed: ANIMATION_CONFIG.NEBULA_PARTICLE_SPEED_MIN + 
               Math.random() * (ANIMATION_CONFIG.NEBULA_PARTICLE_SPEED_MAX - ANIMATION_CONFIG.NEBULA_PARTICLE_SPEED_MIN),
        size: ANIMATION_CONFIG.NEBULA_PARTICLE_SIZE_MIN + 
              Math.random() * (ANIMATION_CONFIG.NEBULA_PARTICLE_SIZE_MAX - ANIMATION_CONFIG.NEBULA_PARTICLE_SIZE_MIN),
        opacity: ANIMATION_CONFIG.NEBULA_PARTICLE_OPACITY_MIN + 
                 Math.random() * (ANIMATION_CONFIG.NEBULA_PARTICLE_OPACITY_MAX - ANIMATION_CONFIG.NEBULA_PARTICLE_OPACITY_MIN)
      });
    }
    return particles;
  }
  
  updateSpecific(delta) {
    // Update cloud phase
    this.cloudPhase += ANIMATION_CONFIG.NEBULA_CLOUD_PHASE_SPEED;
    
    // Update particle positions
    this.particles.forEach(p => {
      p.angle += p.speed * ANIMATION_CONFIG.NEBULA_PARTICLE_ANGLE_SPEED;
      p.distance = (p.distance + p.speed * ANIMATION_CONFIG.NEBULA_PARTICLE_DISTANCE_SPEED) % this.radius;
    });
    
    // Handle depletion fade out
    if (this.isDepleted && this.depletedTime) {
      const timeSinceDepleted = Date.now() - this.depletedTime;
      if (timeSinceDepleted >= RESOURCE_CONFIG.NEBULA_FADE_TIME) {
        this.active = false;
      } else {
        this.opacity = 1.0 - (timeSinceDepleted / RESOURCE_CONFIG.NEBULA_FADE_TIME);
      }
    }
    
    // Clean up invalid gatherers
    if (this.gatherers && this.gatherers.size > 0) {
      const invalidGatherers = [];
      for (const gatherer of this.gatherers) {
        // Remove if: dead, too far away, not in gathering_nebula state, or doesn't have this as target
        const tooFar = getDistance(this, gatherer) > this.radius + 5; // Small buffer
        const wrongState = gatherer.state !== 'gathering_nebula';
        const wrongTarget = gatherer.ai && gatherer.ai.targetNebula !== this;
        
        if (!gatherer.alive || gatherer.health <= 0 || tooFar || wrongState || wrongTarget) {
          invalidGatherers.push(gatherer);
        }
      }
      for (const invalid of invalidGatherers) {
        this.gatherers.delete(invalid);
      }
    }
  }
  
  // Nebulas can be gathered by multiple soldiers
  canBeCollectedBy(soldier) {
    if (this.isDepleted) return false;
    const distance = getDistance(this, soldier);
    return distance <= this.radius && this.gatherers.size < RESOURCE_CONFIG.NEBULA_MAX_GATHERERS;
  }
  
  // Override to add soldier to gatherers
  startCollection(collector) {
    this.gatherers.add(collector);
  }
  
  // Remove soldier from gatherers
  stopCollection(collector) {
    this.gatherers.delete(collector);
  }
  
  // Nebulas aren't collected instantly
  collect(collector) {
    // This shouldn't be called for nebulas
    return { type: 'nebula', amount: 0 };
  }
  
  // Continuous gathering method
  gatherFrom(collector, delta) {
    if (!this.gatherers.has(collector) || this.isDepleted) {
      return 0;
    }
    
    const gatherAmount = Math.min(
      RESOURCE_CONFIG.NEBULA_GATHER_RATE * (delta / 1000),
      this.remainingOrganics
    );
    
    this.remainingOrganics -= gatherAmount;
    
    // Check if depleted
    if (this.remainingOrganics <= 0) {
      this.remainingOrganics = 0;
      this.isDepleted = true;
      this.depletedTime = Date.now();
      
      // Release all gatherers
      for (const gatherer of this.gatherers) {
        this.stopCollection(gatherer);
      }
      this.gatherers.clear();
    }
    
    return gatherAmount;
  }
  
  // Override render to skip base class pulse logic
  render(ctx) {
    if (!this.active) return;
    
    ctx.save();
    ctx.globalAlpha = this.opacity;
    ctx.translate(this.x, this.y);
    this.renderSpecific(ctx, 1);
    ctx.restore();
  }
  
  renderSpecific(ctx, pulse) {
    // Draw cloud base (centered at 0,0)
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius);
    gradient.addColorStop(0, 'rgba(255, 100, 200, 0.3)');
    gradient.addColorStop(0.5, 'rgba(150, 50, 255, 0.2)');
    gradient.addColorStop(1, 'rgba(100, 0, 200, 0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw particles
    this.particles.forEach(p => {
      const x = Math.cos(p.angle) * p.distance;
      const y = Math.sin(p.angle) * p.distance;
      
      ctx.globalAlpha = p.opacity * (1 - p.distance / this.radius);
      ctx.fillStyle = '#ff66cc';
      ctx.fillRect(x - p.size/2, y - p.size/2, p.size, p.size);
    });
    
    // Draw gatherer count and remaining organics
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#fff';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    
    // Show gatherers/slots
    if (this.gatherers.size > 0 || this.isDepleted) {
      ctx.fillText(`${this.gatherers.size}/${RESOURCE_CONFIG.NEBULA_MAX_GATHERERS}`, 
                   0, -this.radius - 5);
    }
    
    // Show remaining percentage
    const percentRemaining = Math.floor((this.remainingOrganics / this.totalOrganics) * 100);
    const color = percentRemaining > 50 ? '#66ff66' : 
                  percentRemaining > 25 ? '#ffff66' : '#ff6666';
    ctx.fillStyle = this.isDepleted ? '#ff0000' : color;
    ctx.fillText(`${percentRemaining}%`, 0, -this.radius - 16);
    
    ctx.globalAlpha = 1;
  }
}